import { useState, useEffect, useMemo } from 'react';
import { EscapeTrackerData, Timeframe, ViewMode } from './types';
import { API_ENDPOINT } from '@Env/variables';
import { createAxiosInstance } from '@Lib/axios/axios';
import { useAuth } from '@Context/CognitoAuthProvider';

export const PRIORITY_ORDER = [
  'Highest',
  'High',
  'Medium',
  'Low',
  'Lowest',
  'No Priority Set',
];
export const OPEN_STATUSES = new Set([
  'Backlog',
  'In Progress',
  'Blocked',
  'Ready for QA',
  'QA In Progress',
  'Code review',
]);
export const CLOSED_STATUSES = new Set(['Done', 'Canceled', 'Ready for PROD']);

export function dateOffset(base: string, days: number): string {
  const d = new Date(base + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function useEscapeData() {
  const [data, setData] = useState<EscapeTrackerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { logout } = useAuth();

  useEffect(() => {
    if (import.meta.env.VITE_BYPASS_AUTH === 'true') {
      fetch('/data/escape-tracker.json')
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<EscapeTrackerData>;
        })
        .then((d) => setData(d))
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
      return;
    }
    const axios = createAxiosInstance(logout, API_ENDPOINT);
    axios
      .get<EscapeTrackerData>('/api/escape-tracker')
      .then((res) => setData(res.data))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [logout]);

  return { data, loading, error };
}

export function useFilteredEscapes(
  data: EscapeTrackerData | null,
  timeframe: Timeframe,
  activeProjects: Set<string>
) {
  return useMemo(() => {
    if (!data) return [];
    let items = data.raw;
    if (timeframe !== 'all' && data.summary.run_date) {
      const cutoffStr = dateOffset(
        data.summary.run_date,
        -parseInt(timeframe, 10)
      );
      items = items.filter((e) => e.created_date >= cutoffStr);
    }
    return items.filter((e) => activeProjects.has(e.project_key));
  }, [data, timeframe, activeProjects]);
}

export function useBuckets(
  items: ReturnType<typeof useFilteredEscapes>,
  view: ViewMode
) {
  return useMemo(() => {
    const b: Record<string, Record<string, number>> = {};
    items.forEach((e) => {
      let key: string;
      if (view === 'weekly') {
        const d = new Date(e.created_date + 'T00:00:00');
        const day = d.getDay();
        const m = new Date(d);
        m.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
        key = m.toISOString().slice(0, 10);
      } else {
        key = e.created_date.slice(0, 7);
      }
      if (!b[key]) b[key] = {};
      b[key][e.project_key] = (b[key][e.project_key] || 0) + 1;
    });
    const keys = Object.keys(b).sort();
    const labels = keys.map((k) => {
      if (view === 'weekly') {
        const d = new Date(k + 'T00:00:00');
        return d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
      }
      const [y, m] = k.split('-');
      return new Date(+y, +m - 1, 1).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
    });
    return { keys, b, labels };
  }, [items, view]);
}

export function useTrendDelta(
  items: ReturnType<typeof useFilteredEscapes>,
  runDate: string
) {
  return useMemo(() => {
    if (!runDate) return { thisWeek: 0, prevWeek: 0, delta: 0 };
    const w1Str = dateOffset(runDate, -7);
    const w2Str = dateOffset(runDate, -14);
    const thisWeek = items.filter(
      (e) => e.created_date >= w1Str && e.created_date <= runDate
    ).length;
    const prevWeek = items.filter(
      (e) => e.created_date >= w2Str && e.created_date < w1Str
    ).length;
    return { thisWeek, prevWeek, delta: thisWeek - prevWeek };
  }, [items, runDate]);
}

export function usePriorityBreakdown(
  items: ReturnType<typeof useFilteredEscapes>
) {
  return useMemo(() => {
    const counts: Record<string, number> = {};
    items.forEach((e) => {
      counts[e.priority] = (counts[e.priority] || 0) + 1;
    });
    return PRIORITY_ORDER.filter((p) => counts[p]).map((p) => ({
      priority: p,
      count: counts[p],
    }));
  }, [items]);
}

export function useStatusRatio(items: ReturnType<typeof useFilteredEscapes>) {
  return useMemo(() => {
    let open = 0,
      closed = 0;
    items.forEach((e) => {
      CLOSED_STATUSES.has(e.status) ? closed++ : open++;
    });
    return { open, closed };
  }, [items]);
}

export function useHeatmapSeries(items: ReturnType<typeof useFilteredEscapes>) {
  return useMemo(() => {
    if (items.length === 0)
      return {
        series: [] as { name: string; data: { x: string; y: number }[] }[],
        weekLabels: [] as string[],
      };

    const b: Record<string, Record<string, number>> = {};
    const projTotals: Record<string, number> = {};

    items.forEach((e) => {
      const d = new Date(e.created_date + 'T00:00:00');
      const day = d.getDay();
      const m = new Date(d);
      m.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
      const key = m.toISOString().slice(0, 10);
      if (!b[key]) b[key] = {};
      b[key][e.project_key] = (b[key][e.project_key] || 0) + 1;
      projTotals[e.project_key] = (projTotals[e.project_key] || 0) + 1;
    });

    const weekKeys = Object.keys(b).sort().slice(-16);
    const weekLabels = weekKeys.map((k) =>
      new Date(k + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    );
    const projects = Object.entries(projTotals)
      .sort((a, bv) => bv[1] - a[1])
      .map(([p]) => p);
    const series = projects.map((proj) => ({
      name: proj,
      data: weekKeys.map((k, i) => ({
        x: weekLabels[i],
        y: b[k]?.[proj] ?? 0,
      })),
    }));

    return { series, weekLabels };
  }, [items]);
}

export function useLabelFrequency(
  items: ReturnType<typeof useFilteredEscapes>,
  topN = 10
) {
  return useMemo(() => {
    const counts: Record<string, number> = {};
    items.forEach((e) => {
      e.labels.forEach((l) => {
        counts[l] = (counts[l] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([label, count]) => ({ label, count }));
  }, [items, topN]);
}

export function useMomentum(
  data: EscapeTrackerData | null,
  activeProjects: Set<string>
) {
  return useMemo(() => {
    if (!data)
      return [] as {
        project: string;
        last30: number;
        prior30: number;
        delta: number;
        arrow: '↑' | '→' | '↓';
      }[];
    const runStr = data.summary.run_date;
    const l30Str = dateOffset(runStr, -30);
    const p30Str = dateOffset(runStr, -60);

    const stats: Record<string, { last30: number; prior30: number }> = {};
    data.raw.forEach((e) => {
      if (!activeProjects.has(e.project_key)) return;
      if (!stats[e.project_key])
        stats[e.project_key] = { last30: 0, prior30: 0 };
      if (e.created_date >= l30Str && e.created_date <= runStr)
        stats[e.project_key].last30++;
      else if (e.created_date >= p30Str && e.created_date < l30Str)
        stats[e.project_key].prior30++;
    });

    return Array.from(activeProjects)
      .map((proj) => {
        const s = stats[proj] ?? { last30: 0, prior30: 0 };
        const delta = s.last30 - s.prior30;
        const arrow: '↑' | '→' | '↓' = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
        return {
          project: proj,
          last30: s.last30,
          prior30: s.prior30,
          delta,
          arrow,
        };
      })
      .sort((a, b) => b.last30 - a.last30);
  }, [data, activeProjects]);
}

export function useMttrByMonth(items: ReturnType<typeof useFilteredEscapes>) {
  return useMemo(() => {
    const byMonth: Record<
      string,
      Record<string, { totalDays: number; count: number }>
    > = {};

    items.forEach((e) => {
      if (!e.resolved_date) return;
      const days = Math.round(
        (new Date(e.resolved_date + 'T00:00:00').getTime() -
          new Date(e.created_date + 'T00:00:00').getTime()) /
          86_400_000
      );
      if (days < 0) return;
      const month = e.created_date.slice(0, 7);
      if (!byMonth[month]) byMonth[month] = {};
      if (!byMonth[month][e.priority])
        byMonth[month][e.priority] = { totalDays: 0, count: 0 };
      byMonth[month][e.priority].totalDays += days;
      byMonth[month][e.priority].count++;
    });

    const months = Object.keys(byMonth).sort();
    const monthLabels = months.map((m) => {
      const [y, mo] = m.split('-');
      return new Date(+y, +mo - 1, 1).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
    });

    const prioritiesWithData = PRIORITY_ORDER.filter((p) =>
      months.some((m) => (byMonth[m]?.[p]?.count ?? 0) >= 3)
    );

    const series = prioritiesWithData.map((p) => ({
      name: p,
      data: months.map((m) => {
        const b = byMonth[m]?.[p];
        if (!b || b.count < 3) return null as unknown as number;
        return Math.round(b.totalDays / b.count);
      }),
    }));

    return {
      months,
      monthLabels,
      series,
      prioritiesWithData,
      buckets: byMonth,
    };
  }, [items]);
}

export function useMttr(items: ReturnType<typeof useFilteredEscapes>) {
  return useMemo(() => {
    const buckets: Record<
      string,
      { totalDays: number; count: number; open: number }
    > = {};

    items.forEach((e) => {
      if (!buckets[e.priority])
        buckets[e.priority] = { totalDays: 0, count: 0, open: 0 };
      if (e.resolved_date) {
        const created = new Date(e.created_date + 'T00:00:00');
        const resolved = new Date(e.resolved_date + 'T00:00:00');
        const days = Math.round(
          (resolved.getTime() - created.getTime()) / 86_400_000
        );
        if (days >= 0) {
          buckets[e.priority].totalDays += days;
          buckets[e.priority].count++;
        }
      } else {
        buckets[e.priority].open++;
      }
    });

    return PRIORITY_ORDER.filter((p) => buckets[p]).map((p) => ({
      priority: p,
      avgDays:
        buckets[p].count > 0
          ? Math.round(buckets[p].totalDays / buckets[p].count)
          : null,
      resolved: buckets[p].count,
      open: buckets[p].open,
    }));
  }, [items]);
}
